import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { ApiError, apiPost } from '@/lib/api';

type Duplicate = {
  id: string;
  name: string;
  dob: string | null;
  team: string | null;
  league: string | null;
};
const fieldClass =
  'focus-ring mt-1 h-10 w-full rounded-control border border-border bg-white px-3 text-sm text-ink';

export function AddProfileModal({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [requestId] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<Duplicate[]>([]);
  const [classification, setClassification] = useState('prospect');
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const fields = new FormData(event.currentTarget);
    const text = (key: string) => String(fields.get(key) ?? '').trim() || null;
    setSaving(true);
    setError(null);
    setDuplicates([]);
    try {
      const result = await apiPost<{ player: { id: string } }>('/api/players', {
        requestId,
        firstName: text('firstName'),
        lastName: text('lastName'),
        classification,
        dob: text('dob'),
        currentTeam: text('currentTeam'),
        currentLeague: text('currentLeague'),
        position: text('position'),
        primaryEmail: text('primaryEmail'),
        cellPhone: text('cellPhone'),
        prospectPriority:
          classification === 'prospect' && text('prospectPriority')
            ? Number(text('prospectPriority'))
            : null,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['players'] }),
        qc.invalidateQueries({ queryKey: ['prospects'] }),
      ]);
      onClose();
      navigate(`/people/${result.player.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.body && typeof e.body === 'object') {
        const body = e.body as {
          candidates?: Duplicate[];
          issues?: Array<{ path: string[]; message: string }>;
        };
        if (e.status === 409 && Array.isArray(body.candidates)) {
          setDuplicates(body.candidates);
          setError(
            'A profile with this name already exists. Open it below, or enter a different birthdate if this is a different person.'
          );
        } else if (body.issues?.length)
          setError(
            body.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join(' · ')
          );
        else setError('Could not save the profile. Your entries are still here; please try again.');
      } else setError('Could not save the profile. Your entries are still here; please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <dialog
      ref={dialog}
      aria-labelledby="add-profile-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onClose();
      }}
      className="m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-xl overflow-y-auto rounded-control border border-border bg-white p-0 text-ink shadow-xl backdrop:bg-black/50"
    >
      <form onSubmit={submit}>
        <header className="flex items-start justify-between gap-3 border-b border-border p-5">
          <div>
            <h2 id="add-profile-title" className="text-xl font-semibold">
              Add profile
            </h2>
            <p className="mt-1 text-sm text-muted">
              Create a client or prospect file. You can add more details after saving.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close add profile"
            disabled={saving}
            onClick={onClose}
            className="focus-ring rounded-control p-1 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </header>
        <fieldset disabled={saving} className="grid gap-4 p-5 sm:grid-cols-2">
          <label className="text-sm font-medium">
            First name <span aria-hidden="true">*</span>
            <input
              autoFocus
              required
              name="firstName"
              maxLength={80}
              autoComplete="given-name"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium">
            Last name <span aria-hidden="true">*</span>
            <input
              required
              name="lastName"
              maxLength={80}
              autoComplete="family-name"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium">
            Profile type
            <select
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
              name="classification"
              className={fieldClass}
            >
              <option value="prospect">Prospect / junior</option>
              <option value="pro_men">Pro client — men</option>
              <option value="pro_women">Pro client — women</option>
            </select>
          </label>
          <label className="text-sm font-medium">
            Birthdate
            <input
              type="date"
              name="dob"
              max={new Date().toISOString().slice(0, 10)}
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium">
            Current team
            <input name="currentTeam" maxLength={160} className={fieldClass} />
          </label>
          <label className="text-sm font-medium">
            Current league
            <input
              name="currentLeague"
              list="profile-leagues"
              maxLength={40}
              placeholder="e.g. OHL"
              className={fieldClass}
            />
            <datalist id="profile-leagues">
              {[
                'NHL',
                'AHL',
                'ECHL',
                'PWHL',
                'NCAA',
                'OHL',
                'QMJHL',
                'WHL',
                'USHL',
                'NAHL',
                'BCHL',
                'NTDP',
                'YOUTH',
              ].map((league) => (
                <option key={league} value={league} />
              ))}
            </datalist>
          </label>
          <label className="text-sm font-medium">
            Position
            <select name="position" className={fieldClass}>
              <option value="">Not set</option>
              {['C', 'LW', 'RW', 'F', 'D', 'G'].map((position) => (
                <option key={position}>{position}</option>
              ))}
            </select>
          </label>
          {classification === 'prospect' && (
            <label className="text-sm font-medium">
              Prospect priority
              <select name="prospectPriority" className={fieldClass}>
                <option value="">Not set</option>
                <option value="0">P0</option>
                <option value="1">P1</option>
                <option value="2">P2</option>
              </select>
            </label>
          )}
          <label className="text-sm font-medium">
            Email
            <input
              type="email"
              name="primaryEmail"
              maxLength={254}
              autoComplete="email"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium">
            Phone
            <input
              type="tel"
              name="cellPhone"
              maxLength={40}
              autoComplete="tel"
              className={fieldClass}
            />
          </label>
        </fieldset>
        {error && (
          <div
            role="alert"
            className="mx-5 mb-4 rounded-control border border-flag-warn/30 bg-flag-warn/5 p-3 text-sm"
          >
            <p>{error}</p>
            {duplicates.map((p) => (
              <Link
                key={p.id}
                to={`/people/${p.id}`}
                onClick={onClose}
                className="mt-2 block font-semibold underline"
              >
                {p.name} ·{' '}
                {[p.team, p.league, p.dob].filter(Boolean).join(' · ') || 'Open existing profile'}
              </Link>
            ))}
          </div>
        )}
        <footer className="flex justify-end gap-3 border-t border-border p-5">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="focus-ring rounded-control border border-border px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="focus-ring rounded-control bg-bartlett px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Creating profile…' : 'Create profile'}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
